/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-09
 */

// Add third-party dependencies.
import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  AttachMoney as PricingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import PricingCard from './components/PricingCard';
import BillingToggle from './components/BillingToggle';
import Header from '../../layout/Header';
import {
  useGetSubscriptionPlansQuery,
  useSelectPlanMutation,
  useGetCurrentSubscriptionQuery,
} from '../../../features/auth/authApi';
import { SubscriptionPlan } from '../../../types/subscription';
import { useSessionAuth } from '../../../hooks/useSessionAuth';
import AddPaymentMethodModal from '../Billing/components/AddPaymentMethodModal';
import DowngradeViolationDialog from '../../subscription/DowngradeViolationDialog';
import { YouTubeVideoButton } from '../../common';

/**
 * Pricing plan data structure.
 */
interface PricingPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  badge?: string;
  features: string[];
  description: string;
  ctaText: string;
  ctaVariant?: 'contained' | 'outlined';
  popular?: boolean;
  premium?: boolean;
}

/**
 * Pricing page component that showcases pricing plans and information.
 *
 * @returns {React.ReactElement} The pricing page component
 *
 * @example
 * return <PricingPage />;
 */
const PricingPage: React.FC = () => {
  const { t } = useTranslation('pricing');
  const theme = useTheme();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);

  // Authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Fetch subscription plans from API
  const {
    data: apiResponse,
    isLoading: plansLoading,
    error: plansError,
  } = useGetSubscriptionPlansQuery();

  // Extract plans array from response (API returns paginated response with "results" key)
  const apiPlans = Array.isArray(apiResponse)
    ? apiResponse
    : (apiResponse as any)?.results || [];

  // Get current subscription for comparison (only for authenticated users)
  const { data: currentSubscription } = useGetCurrentSubscriptionQuery(
    undefined,
    {
      skip: !isAuthenticated,
    }
  );

  // Plan selection mutation
  const [selectPlan] = useSelectPlanMutation();

  // Modal and dialog state for validation
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [violationDialogOpen, setViolationDialogOpen] = useState(false);
  const [violations, setViolations] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessingPlan, setIsProcessingPlan] = useState(false);

  /**
   * Store plan selection in sessionStorage for anonymous users
   */
  const storePlanSelection = (planId: string, billingCycle: string) => {
    const planData = {
      selectedPlan: planId,
      billingCycle,
      timestamp: Date.now(),
    };
    sessionStorage.setItem('selectedPlan', JSON.stringify(planData));
  };

  // Convert API plans to UI format
  const convertApiPlanToUIPlan = (apiPlan: SubscriptionPlan): PricingPlan => {
    // Determine plan characteristics
    const isGrowthPlan = apiPlan.slug === 'growth';
    const isProPlan = apiPlan.slug === 'pro';
    const isFreePlan = apiPlan.slug === 'free';

    // Generate features list based on plan limits
    const features: string[] = [];

    if (apiPlan.max_owned_studios === 0) {
      features.push('Unlimited studios');
    } else {
      features.push(
        `Up to ${apiPlan.max_owned_studios} studio${apiPlan.max_owned_studios > 1 ? 's' : ''}`
      );
    }

    if (apiPlan.max_users_per_studio === 0) {
      features.push('Unlimited users');
    } else {
      features.push(
        `Up to ${apiPlan.max_users_per_studio} user${apiPlan.max_users_per_studio > 1 ? 's' : ''}`
      );
    }

    // Add custom features from API (format snake_case to Title Case)
    const formattedFeatures = apiPlan.features.map(feature =>
      feature
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    );
    features.push(...formattedFeatures);

    // Add watermark information
    if (isFreePlan) {
      features.push('tools.dance watermark on public pages');
    } else {
      features.push('No watermark on public pages');
    }

    // Check if user has already used their trial
    // Only check if we have subscription data and user is authenticated
    const hasUsedTrial =
      isAuthenticated && currentSubscription?.has_used_trial === true;

    // Add trial information if applicable
    // Only show trial feature text for new users who haven't used their trial
    if (apiPlan.has_trial && apiPlan.trial_days > 0 && !hasUsedTrial) {
      features.push(`${apiPlan.trial_days}-day free trial included`);
    }

    // Determine CTA text based on plan type and trial eligibility
    let ctaText: string;
    if (isFreePlan) {
      ctaText = 'Start Free';
    } else if (
      (isGrowthPlan || isProPlan) &&
      apiPlan.has_trial &&
      !hasUsedTrial
    ) {
      // Show trial CTA only if user hasn't used their trial
      ctaText = `Start ${apiPlan.trial_days}-Day Trial`;
    } else {
      ctaText = `Choose ${apiPlan.name}`;
    }

    // Determine badge text
    let badge: string | undefined;
    if (isGrowthPlan) {
      badge = 'Most Popular';
    } else if (isProPlan) {
      badge = 'Premium';
    } else {
      badge = undefined;
    }

    return {
      id: apiPlan.slug,
      name: apiPlan.name,
      monthlyPrice: apiPlan.price_monthly / 100, // Convert cents to dollars
      yearlyPrice: apiPlan.price_yearly / 100, // Convert cents to dollars
      badge,
      features,
      description: apiPlan.description,
      ctaText,
      ctaVariant: isGrowthPlan || isProPlan ? 'contained' : 'outlined',
      popular: isGrowthPlan,
      premium: isProPlan,
    };
  };

  // Convert API plans to UI format, fallback to hardcoded data if API fails
  let pricingPlans: PricingPlan[];

  if (apiPlans.length > 0) {
    pricingPlans = apiPlans.map(convertApiPlanToUIPlan);
  } else {
    pricingPlans = [
      // Fallback hardcoded plans
      {
        id: 'free',
        name: 'Free',
        monthlyPrice: 0,
        yearlyPrice: 0,
        features: [
          '1 studio',
          '1 user',
          'Basic set of tools',
          'tools.dance watermark on public pages',
          'Perfect for trying it out',
        ],
        description: 'Get started with basic features',
        ctaText: 'Start Free',
        ctaVariant: 'outlined',
      },
      {
        id: 'starter',
        name: 'Starter',
        monthlyPrice: 10,
        yearlyPrice: 100,
        features: [
          'Up to 1 studio',
          'Up to 3 users',
          'Essential tools for small teams',
          'No watermark',
          'Great for solo teachers or side-hustle studios',
        ],
        description: 'Perfect for small studios',
        ctaText: 'Choose Starter',
        ctaVariant: 'outlined',
      },
      {
        id: 'growth',
        name: 'Growth',
        monthlyPrice: 15,
        yearlyPrice: 150,
        badge: 'Most Popular',
        popular: true,
        features: [
          'Up to 5 studios',
          'Up to 25 users',
          'Advanced tools for busy studios',
          'No watermark',
          '7-day free trial included',
          'The sweet spot for most studio owners',
        ],
        description: 'Best value for growing studios',
        ctaText: 'Start 7-Day Trial',
        ctaVariant: 'contained',
      },
      {
        id: 'pro',
        name: 'Pro',
        monthlyPrice: 25,
        yearlyPrice: 250,
        badge: 'Premium',
        premium: true,
        features: [
          'Unlimited studios',
          'Unlimited users',
          'Access to all tools',
          'No watermark',
          '7-day free trial included',
          'Priority support & early access to new features',
          'Best for established or multi-location studios',
        ],
        description: 'Everything you need at scale',
        ctaText: 'Start 7-Day Trial',
        ctaVariant: 'contained',
      },
    ];
  }

  /**
   * Handle getting started navigation.
   */
  const handleGetStarted = () => {
    navigate('/register');
  };

  /**
   * Handle plan selection with comprehensive validation.
   */
  const handlePlanSelect = async (planId: string) => {
    const billingCycle = isAnnual ? 'yearly' : 'monthly';

    // If user is not authenticated, store selection and navigate to register
    if (!isAuthenticated || authLoading) {
      storePlanSelection(planId, billingCycle);
      navigate('/register', {
        state: {
          selectedPlan: planId,
          billingCycle,
        },
      });
      return;
    }

    // Clear previous messages
    setSuccessMessage(null);
    setErrorMessage(null);
    setIsProcessingPlan(true);

    try {
      // Find the selected plan from the UI pricing plans (includes fallback data)
      const selectedPlan = pricingPlans.find(plan => plan.id === planId);
      if (!selectedPlan) {
        setErrorMessage('Selected plan not found. Please try again.');
        setIsProcessingPlan(false);
        return;
      }

      // Determine if this is an upgrade or downgrade using plan hierarchy
      const planHierarchy = ['free', 'starter', 'growth', 'pro'];
      const currentPlanIndex = planHierarchy.indexOf(
        currentSubscription?.plan?.slug || 'free'
      );
      const selectedPlanIndex = planHierarchy.indexOf(selectedPlan.id);

      // If plan hierarchy doesn't work, fall back to price comparison
      let isUpgrade: boolean;
      if (currentPlanIndex >= 0 && selectedPlanIndex >= 0) {
        isUpgrade = selectedPlanIndex > currentPlanIndex;
      } else {
        const currentPlanPrice = currentSubscription?.plan?.price_monthly || 0;
        const selectedPlanPrice = isAnnual
          ? selectedPlan.yearlyPrice * 100
          : selectedPlan.monthlyPrice * 100;
        isUpgrade = selectedPlanPrice > currentPlanPrice;
      }

      console.log('Plan change details:', {
        currentPlan: currentSubscription?.plan?.name,
        currentPlanSlug: currentSubscription?.plan?.slug,
        currentPlanIndex,
        selectedPlan: selectedPlan.name,
        selectedPlanId: selectedPlan.id,
        selectedPlanIndex,
        isUpgrade,
      });

      // Call the API with proper isUpgrade flag
      await selectPlan({
        plan_slug: planId,
        billing_cycle: billingCycle,
        isUpgrade,
      }).unwrap();

      // Show success message and stay on pricing page
      setSuccessMessage(
        `Successfully changed to ${selectedPlan.name} plan! Your new features are now available.`
      );
      setIsProcessingPlan(false);
    } catch (error: any) {
      console.error('Failed to select plan:', error);
      console.error('Error data:', error?.data);
      console.error('Error message:', error?.data?.error || error?.message);

      setIsProcessingPlan(false);

      // Check for downgrade violations
      if (
        error?.data?.downgrade_violations &&
        Array.isArray(error.data.downgrade_violations)
      ) {
        setViolations(error.data.downgrade_violations);
        setViolationDialogOpen(true);
        return;
      }

      // Check if this is a payment method validation error
      const errorMsg = error?.data?.error || error?.message || '';
      const isPaymentMethodError =
        errorMsg.includes('payment method is required') ||
        errorMsg.includes('payment method') ||
        errorMsg.includes('billing readiness');

      if (isPaymentMethodError) {
        setPaymentMethodModalOpen(true);
        return;
      }

      // Handle other errors with inline message
      setErrorMessage(
        error?.data?.error ||
          error?.message ||
          'Failed to change plan. Please try again or contact support.'
      );
    }
  };

  // Handle payment method modal close
  const handlePaymentMethodModalClose = () => {
    setPaymentMethodModalOpen(false);
  };

  // Handle payment method success - retry plan selection
  const handlePaymentMethodSuccess = () => {
    setPaymentMethodModalOpen(false);
    // The user can manually retry by clicking the plan again
    setSuccessMessage(
      'Payment method added successfully! You can now select your plan.'
    );
  };

  // Handle violation dialog close
  const handleViolationDialogClose = () => {
    setViolationDialogOpen(false);
    setViolations([]);
  };

  // Handle navigation to billing page
  const handleViewBillingDetails = () => {
    navigate('/billing');
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />

      {/* Beta Notification Banner */}
      <Alert
        severity="info"
        sx={{
          backgroundColor: 'secondary.main',
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: '1.1rem',
          textAlign: 'center',
          borderRadius: 0,
          '& .MuiAlert-icon': {
            color: '#FFFFFF',
          },
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        {t('beta.banner')}
      </Alert>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
            maxWidth: 800,
            mx: 'auto',
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            {t('hero.title')}
          </Typography>

          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              mb: 2,
              fontWeight: 400,
            }}
          >
            {t('hero.subtitle')}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('hero.description')}
          </Typography>

          {isAuthenticated ? (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'inline-block',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Learn how to upgrade your plan and unlock premium features.
              </Typography>
              <YouTubeVideoButton
                videoId="5vt_utY1LDw"
                videoTitle="How to Upgrade Your Plan on tools.dance"
                buttonText="Watch Upgrade Tutorial"
                variant="contained"
                size="large"
                fullWidth
              />
            </Box>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              startIcon={<PricingIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              {t('hero.getStarted')}
            </Button>
          )}
        </Box>

        {/* Billing Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <BillingToggle isAnnual={isAnnual} onToggle={setIsAnnual} />
        </Box>

        {/* Success/Error Messages */}
        {successMessage && (
          <Box sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            <Alert severity="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
              <Button
                onClick={handleViewBillingDetails}
                sx={{ ml: 2 }}
                variant="outlined"
                size="small"
              >
                View Billing Details
              </Button>
            </Alert>
          </Box>
        )}

        {errorMessage && (
          <Box sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            <Alert severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          </Box>
        )}

        {/* Pricing Cards Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 8,
            justifyItems: 'center',
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {plansLoading && (
            // Loading state
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('messages.loading')}
              </Typography>
            </Box>
          )}

          {!plansLoading && plansError && (
            // Error state - show fallback plans but with warning
            <>
              <Box sx={{ gridColumn: '1 / -1', mb: 3 }}>
                <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
                  {t('messages.errorLoading')}
                </Alert>
              </Box>
              {pricingPlans.map(plan => (
                <Box key={plan.id} sx={{ width: '100%', maxWidth: 320 }}>
                  <PricingCard
                    plan={plan}
                    isAnnual={isAnnual}
                    isProcessing={isProcessingPlan}
                    onSelect={() => handlePlanSelect(plan.id)}
                  />
                </Box>
              ))}
            </>
          )}

          {!plansLoading && !plansError && (
            // Normal state
            <>
              {pricingPlans.map(plan => (
                <Box key={plan.id} sx={{ width: '100%', maxWidth: 320 }}>
                  <PricingCard
                    plan={plan}
                    isAnnual={isAnnual}
                    isProcessing={isProcessingPlan}
                    onSelect={() => handlePlanSelect(plan.id)}
                  />
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* Feature Highlights */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 600,
            }}
          >
            {t('whyChoose.title')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 4,
              maxWidth: 1000,
              mx: 'auto',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CheckIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t('whyChoose.lightweight.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whyChoose.lightweight.description')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <CheckIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t('whyChoose.saveTime.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whyChoose.saveTime.description')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <CheckIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t('whyChoose.modern.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whyChoose.modern.description')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <CheckIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t('whyChoose.flexible.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whyChoose.flexible.description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8, maxWidth: 800, mx: 'auto' }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 600,
            }}
          >
            {t('faq.title')}
          </Typography>

          {[
            'annualDiscount',
            'switchPlans',
            'replacement',
            'trials',
            'proIncludes',
          ].map(key => (
            <Accordion
              key={`faq-${key}`}
              sx={{
                mb: 2,
                '&:before': {
                  display: 'none',
                },
                boxShadow: 1,
                borderRadius: 2,
                '&:not(:last-child)': {
                  borderBottom: 0,
                },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 3,
                  py: 2,
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0',
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(`faq.items.${key}.question`)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  {t(`faq.items.${key}.answer`)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Closing CTA */}
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            backgroundColor:
              theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            {t('closingCta.title')}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            {t('closingCta.description')}
          </Typography>

          {isAuthenticated ? (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'inline-block',
                maxWidth: 400,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Learn how to upgrade your plan and unlock premium features.
              </Typography>
              <YouTubeVideoButton
                videoId="5vt_utY1LDw"
                videoTitle="How to Upgrade Your Plan on tools.dance"
                buttonText="Watch Upgrade Tutorial"
                variant="contained"
                size="large"
                fullWidth
              />
            </Box>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              {t('closingCta.button')}
            </Button>
          )}
        </Box>
      </Container>

      {/* Payment Method Modal */}
      <AddPaymentMethodModal
        open={paymentMethodModalOpen}
        onClose={handlePaymentMethodModalClose}
        onSuccess={handlePaymentMethodSuccess}
      />

      {/* Downgrade Violation Dialog */}
      <DowngradeViolationDialog
        open={violationDialogOpen}
        onClose={handleViolationDialogClose}
        targetPlan={
          Array.isArray(apiPlans)
            ? apiPlans.find(p => p.slug === violations[0]) || null
            : null
        }
        violations={violations}
      />
    </Box>
  );
};

export default PricingPage;
